import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PiFilePdf } from "react-icons/pi";

import Dropzone from "@/components/common/Dropzone/Dropzone";
import FileScanner from "@/components/common/Dropzone/FileScanner";
import MotionText from "@/components/common/Motion/MotionText";
import MotionDiv from "@/components/common/Motion/MotionDiv";
import HeaderBack from "@/components/dashboard/Header/HeaderBack";
import ButtonLink from "@/components/common/Button/ButtonLink";
import { UberIcon } from "@/components/common/UberIcon";

import documentsAPI from "@/services/api/document";
import receiptsAPI from "@/services/api/receipts";
import ProgressBar from "@/components/common/ProgressBar/ProgressBar";

import { useKycStore } from "@/store/useKycStore";
import { useUberStore } from "@/store/useUberStore";
import { useUserStore } from "@/store/useUserStore";
import { usePageSetup } from "@/hooks/usePageSetup";

export const UberBalancePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('uber.pageTitle.balance'))
  const user = useUserStore((state) => state.user)

  /* Unlike Bolt, a disabled uber_status does not block access,
     it only hides the CSV trips step */
  const csvStepEnabled = user?.uber_status === 'enabled'

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(1), [])

  const navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeFile = async (file: File) => {
    try {
      if (file.type === 'application/pdf') {
        const result = await documentsAPI.analyzeIncome(file);
        return result;
      } else if (file.type === 'image/jpeg' || file.type === 'image/png') {
        const result = await receiptsAPI.analyzeIncome(file);
        return result;
      } else {
        throw new Error(t('validation.unsupportedFileType'));
      }
    } catch {
      setError(t('uber.errors.recognitionFailed'));
      setIsScanning(false);
      return null;
    }
  };

  const setBalanceAnalysis = useUberStore((state) => state.setBalanceAnalysis)

  const handleFileSelect = async (file: File) => {
    setIsScanning(true);
    setError(null);

    const analysis = await analyzeFile(file);

    if (analysis) {
      const date = new Date(analysis.reference.date);

      if (
        date.getFullYear() === new Date().getFullYear() &&
        date.getMonth() === new Date().getMonth()
      ) {
        setError(t('uber.errors.currentMonthNotEnded'));
      } else {
        setBalanceAnalysis(analysis)
        navigate(csvStepEnabled ? "/uber/trips" : "/uber/income");
      }
    }
  };

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack>
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={csvStepEnabled ? 3 : 2}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col px-5 pb-[10rem]">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-1"
          delay={0.1}
        >
          {t('uber.balance.title')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-8"
          delay={0.2}
        >
          {t('uber.balance.description')}
        </MotionText>
        <MotionDiv
          className="bg-gray-100 p-4 rounded-lg flex flex-row gap-2 mb-4"
          delay={0.2}
        >
          <PiFilePdf className="h-6 w-6 text-black shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-sm text-zinc-700 font-semibold">
              {t('uber.balance.downloadStep')}
            </p>
            <p className="text-sm text-zinc-700">
              {t('uber.balance.downloadInstructions')}
            </p>
          </div>
        </MotionDiv>
        {error && (
          <MotionText
            as="p"
            className="text-red-500 text-sm"
            delay={0.2}
          >
            {error}
          </MotionText>
        )}
        <MotionDiv
          className="flex flex-col gap-2"
          delay={0.3}
        >
          {isScanning ? (
            <FileScanner isScanning={isScanning} />
          ) : (
            <Dropzone
              onFileSelect={handleFileSelect}
              maxSize={10 * 1024 * 1024}
              buttonText={t('uber.balance.selectFile')}
            >
              <UberIcon size="lg" className="mb-2" />
            </Dropzone>
          )}
        </MotionDiv>
      </div>
      <motion.div className="px-5 pb-8 flex flex-col gap-2 absolute bottom-0 w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.2,
          ease: "easeOut"
        }}
        whileTap={{ scale: 0.98 }}
      >
        <ButtonLink to={'/uber/income'} variant='secondary'>
          {t('uber.balance.enterWithoutDocument')}
        </ButtonLink>
      </motion.div>
    </div>
  );
};

export default UberBalancePage;
